import { IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { parsePeriodRange } from '@utils/parsePeriodRange';
import { getClientTz } from '@utils/getClientTz';
import { userRolesDoPedido } from '@utils/userRolesDoPedido';
import { InputSelect } from '@vibTheme/components/_base/Input';
import { InputCommunities } from '@vibTheme/components/_base/Input/InputCommunities';
import { VibModal } from '@vibTheme/components/VibModal';
import { CustomPeriodRange } from '@components/Dashboard/CustomPeriodRange';
import { DashboardFilters } from '@interfaces/dashboard';
import {
  Groups,
  ParsedUserRole,
  parseUserRole,
  UserCommunity,
  UserRole,
} from '@interfaces/auth';
import useGroupsContext from '@contexts/GroupsContext/hook';
import {
  getSelectedFilters,
  setSelectedFilters,
} from '@repository/selectedFilters';
import { resolveInitialPeriodRange } from '@utils/resolveInitialPeriodRange';
import { resolveHandedOverPeriod } from '@utils/resolveHandedOverPeriod';

type Props = {
  /** Namespace for the remembered selection. Screens that drill into each other share one. */
  selectedFiltersPage: string;
  onChange: (filters: DashboardFilters) => void;
  /**
   * Se esta tela sabe servir a AUSÊNCIA de papel — o modo "Tudo o que eu alcanço" (#360).
   *
   * Falso por padrão, e a razão é que o modo é capacidade de quem CONSOME os filtros, não deles. Só
   * o corpo novo do painel particiona a seleção por escopo resolvido e monta uma família por escopo;
   * os outros consumidores tratam qualquer valor diferente de `AFFILIATED` como produtor:
   *
   *   - o painel LEGADO (`DashboardVersions.PREVIOUS`) manda ids mistos para widgets de produtor;
   *   - os cinco detalhes guardam com `filters.userRole !== 'AFFILIATED'`, e três deles pedem
   *     relatório que a `report-api` recusa para afiliado.
   *
   * Oferecer a opção onde ela não é servida troca "tela que explica" por "toast de erro e tela
   * vazia". Quem passa `true` está afirmando que sabe particionar.
   */
  admiteSemPapel?: boolean;
  /**
   * As comunidades que a tela tem como servir no modo sem papel, já resolvidas por ela.
   *
   * Sem isto a lista do modo é recortada só por `dashboard:view`, e passa a afirmar que inclui uma
   * comunidade cujo escopo nenhuma família admite — que todo agregado depois omite em silêncio.
   * Quem sabe esse critério é o painel, então ele manda a resposta.
   */
  comunidadesServidas?: number[];
  /**
   * Selection to open with, handed over by the screen that linked here.
   *
   * Without it a drill-down lands on its own default period and answers about a different cohort than the
   * card that was clicked - the detail would not explain the summary.
   */
  initialSelection?: {
    periodRange?: string;
    startDate?: Date;
    endDate?: Date;
    previousPeriodStartDate?: Date;
    previousPeriodEndDate?: Date;
    userRole?: UserRole;
    botConfigIds?: number[];
  };
};

type Form = {
  /**
   * O papel escolhido, OU a string vazia de "Tudo o que eu alcanço".
   *
   * O tipo admite o vazio porque o campo admite: era `UserRole` e mentia — o valor do modo sem papel
   * já circulava aqui, e o compilador só reclamou quando a validação do papel lembrado estreitou o
   * resto da expressão. Quem lê o estado da tela normaliza vazio para `undefined`.
   */
  userRole: UserRole | typeof SEM_PAPEL;
  periodRange: string;
};

/**
 * Role, communities and period, the filter set every conversion figure is scoped by.
 *
 * Extracted from the dashboard page so the detail screen shares it rather than carrying a second copy.
 * It is sixty lines of stateful form - debounced auto-submit, a custom-range modal, a remembered role -
 * and two copies would drift the moment one of them got a fix.
 */
export function ConversionFilters({
  selectedFiltersPage,
  onChange,
  initialSelection,
  admiteSemPapel = false,
  comunidadesServidas,
}: Props) {
  const { groups, communities } = useGroupsContext();
  /*
    Papel explícito continua vencendo — de link compartilhado ou da seleção lembrada —, e a ausência
    dele passa a ser "tudo o que eu alcanço" em vez do primeiro papel que a pessoa tem.
  */
  /*
    O papel LEMBRADO também é validado, e é o par da validação da seleção lembrada.

    Mesma assimetria, um campo ao lado: o papel vindo de link passa pelo `parseUserRole`, e o guardado
    no navegador era restaurado cru. Entre visitas a coprodução pode terminar e a afiliação pode ser
    desativada — e aí o papel guardado não tem comunidade nenhuma, a lista abre vazia e o painel abre
    em branco, mesmo havendo comunidade servível por grant.

    Validado contra os BALDES atuais, e não só contra o vocabulário: papel que existe mas que a pessoa
    não tem mais é exatamente o caso que produz lista vazia.
  */
  const papelLembrado = useMemo(() => {
    const guardado = parseUserRole(
      getSelectedFilters(selectedFiltersPage)?.userRoles?.[0],
      supportedUserRoles,
    );

    return guardado && temOPapel(communities, guardado) ? guardado : undefined;
  }, [communities, selectedFiltersPage]);

  /*
    As opções são os papéis que a pessoa TEM, mais "Tudo o que eu alcanço".

    Oferecer papel que ela não tem é oferecer lista vazia: escolher troca o recorte do seletor de
    permissão para papel, e não sobra comunidade nenhuma — painel em branco com o aviso de "selecione
    pelo menos uma comunidade". O colaborador puro, que o portão passou a admitir, via os três.

    A `userRoleOptions` de módulo continua inteira, e é de propósito: ela é a fonte do
    `supportedUserRoles`, que valida papel vindo de LINK. Um papel que a pessoa não tem hoje pode
    vir num link legítimo e ser recusado depois pela lista — recusar no vocabulário confundiria as
    duas coisas.
  */
  const opcoesDisponiveis = useMemo(
    () =>
      (admiteSemPapel ? opcoesDeFuncao : userRoleOptions).filter(
        (opcao) =>
          !opcao.value || temOPapel(communities, opcao.value as UserRole),
      ),
    [admiteSemPapel, communities],
  );

  const userRoleDefaultValue = useMemo(
    () =>
      initialSelection?.userRole ||
      papelLembrado ||
      (admiteSemPapel ? SEM_PAPEL : resolveUserRoleDefaultValue(groups)),
    [groups, papelLembrado, initialSelection?.userRole, admiteSemPapel],
  );

  const { handleSubmit, control, watch } = useForm<Form>({
    defaultValues: { userRole: userRoleDefaultValue },
  });
  const userRole = watch('userRole');

  /*
    **O que se guarda: o que veio de FORA e o que a pessoa MUDOU. Nunca o que a tela deduziu.**

    É a mesma regra que o `InputCommunities` aplica aos ids, e escrever as duas iguais é o ponto: as
    três rodadas de revisão que chegaram aqui foram, cada uma, meia regra aplicada a um campo.

      - de fora (o `role` de um link compartilhado)  -> grava, como os ids entregues já são;
      - mudança da pessoa                            -> grava;
      - papel de partida deduzido pela tela          -> NÃO grava.

    O terceiro é o que fazia estrago: o detalhe não serve o modo sem papel, cai no primeiro papel
    disponível, e gravar esse papel apagava a escolha "Tudo o que eu alcanço" feita no painel — a
    volta mostrava outra fatia financeira sem dizer nada.

    E o primeiro, que eu tinha suprimido junto na rodada anterior, é o oposto: papel de link é
    intenção declarada. Suprimi-lo deixava o link gravar os ids e não o papel, e a volta ao painel
    juntava papel velho com ids novos — que a poda então descartava por inelegíveis, caindo em outro
    escopo ainda.
  */
  const jaMontou = useRef(false);

  useEffect(() => {
    const naMontagem = !jaMontou.current;
    jaMontou.current = true;

    if (naMontagem && !initialSelection?.userRole) return;

    setSelectedFilters(selectedFiltersPage, {
      userRoles: userRolesDoPedido(userRole || undefined),
    });
  }, [userRole, selectedFiltersPage, initialSelection?.userRole]);

  /**
   * The filter state lives here, not in the parent, because CustomPeriodRange takes a real state setter
   * and calls it with the UPDATER form - setDashboardFilters(prev => ({ ...prev, ...dates })). Handing it
   * a plain callback would pass a function where the filters object belongs, and the custom range would
   * silently produce garbage. Initialised with a complete object for the same reason: the modal spreads
   * prev, and spreading null would drop the selected communities and role.
   */
  const handedOverDates = resolveHandedOverPeriod(
    initialSelection?.startDate,
    initialSelection?.endDate,
    initialSelection?.previousPeriodStartDate,
    initialSelection?.previousPeriodEndDate,
  );

  /**
   * Only a KNOWN range name is honoured. parsePeriodRange looks the name up in an enum and an unknown one
   * yields invalid dates, so a stale or hand-edited URL would send null dates to the API instead of
   * falling back. Explicit dates with no valid name are a custom range, which is what CUSTOM means here.
   */
  const initialPeriodRange = useMemo(
    () =>
      resolveInitialPeriodRange(
        initialSelection?.periodRange,
        Boolean(handedOverDates),
      ),
    // eslint-disable-next-line
    [],
  );
  /**
   * The NAMED range this screen was handed, kept until the producer picks a different one.
   *
   * A drill-down link carries both descriptions of the same period: the range name and the dates the
   * source had already resolved from it. The name wins above, so the mount submit below lands on the
   * unnamed branch of onSubmit and recomputes the dates from the name - throwing away the ones the link
   * carried. Both sides call parsePeriodRange with the same name, so the result is identical until the
   * clock crosses a boundary between the two renders. Then it is not: a tab opened at 23h58 and clicked
   * at 00h02 hands over one window and measures another, and CURRENT_MONTH clicked just after midnight on
   * the first opens a screen holding two minutes of data while still labelled "Mês atual".
   *
   * Nothing warns, because the destination is not wrong about anything it says - it is answering about a
   * different period than the card that was clicked.
   *
   * So a handed-over named range keeps its dates, the same way CUSTOM already does, and drops them the
   * moment the producer touches the period selector or presses refresh - at which point recomputing is
   * exactly right, because the selection is theirs and no longer has a card to agree with.
   */
  const handedOverNamedRange = useRef(
    handedOverDates && initialPeriodRange !== 'CUSTOM'
      ? initialPeriodRange
      : null,
  );

  const [filters, setFilters] = useState<DashboardFilters>({
    botConfigIds: [],
    clientTz: getClientTz(),
    periodRange:
      initialPeriodRange === 'CUSTOM' ? undefined : initialPeriodRange,
    ...parsePeriodRange(
      (initialPeriodRange === 'CUSTOM'
        ? 'LAST_30_DAYS'
        : initialPeriodRange) as never,
    ),
    // Explicit dates win: a custom range has no named period to resolve from.
    ...(handedOverDates ?? {}),
    /*
      `|| undefined` porque o valor do formulário para "tudo o que eu alcanço" é string vazia, e o
      estado que sai daqui é lido como `UserRole | undefined`. Todo consumidor testa veracidade, então
      vazio já se comportava como ausente — normalizar aqui é o que impede o tipo de mentir.
    */
    userRole: userRoleDefaultValue || undefined,
  } as DashboardFilters);

  useEffect(() => {
    onChange(filters);
    // eslint-disable-next-line
  }, [filters]);

  const formRef = useRef<HTMLFormElement>(null);
  let onChangeCallBackTimeout: NodeJS.Timeout;

  // Submits once on mount so the screen loads with the default selection instead of empty.
  useEffect(() => {
    formRef.current?.requestSubmit();
  }, []);

  const formFieldsOnChangeCallback = () => {
    clearTimeout(onChangeCallBackTimeout);
    onChangeCallBackTimeout = setTimeout(() => {
      formRef.current?.requestSubmit();
    }, 500);
  };

  const onSubmit = (data: any) => {
    /**
     * Two selections keep whatever dates are already in state instead of resolving them again: a custom
     * range, which has no name to resolve from, and a named range this screen was HANDED - see
     * handedOverNamedRange for why recomputing that one silently answers about a different period.
     *
     * Recomputing here is what replaced a handed-over range with the last 30 days.
     *
     * The rest of the selection still syncs. Returning outright, as this did before, meant a community or
     * role change was ignored for as long as a custom range was selected.
     */
    const keepsHandedOverDates =
      data.periodRange === 'CUSTOM' ||
      data.periodRange === handedOverNamedRange.current;

    if (keepsHandedOverDates) {
      /**
       * A custom range REPLACES whatever named range was selected, so that name stops describing the
       * dates and is dropped from the filters entirely.
       *
       * CustomPeriodRange replaces only the dates, so the name selected before it stayed in state and
       * reportLinkParams emitted it alongside the custom range - a drill-down link that describes itself
       * as "Ultimos 30 dias" while carrying dates the producer typed by hand.
       */
      const replacesNamedRange = data.periodRange === 'CUSTOM';

      setFilters((current) => ({
        ...current,
        periodRange: replacesNamedRange ? undefined : current.periodRange,
        botConfigIds: data.botConfigIds,
        userRole: data.userRole || undefined,
        clientTz: getClientTz(),
      }));
      return;
    }

    setFilters({
      ...data,
      ...parsePeriodRange(data.periodRange),
      clientTz: getClientTz(),
    });
  };

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const periodRange = watch('periodRange');
  /**
   * Suppressed for the initial CUSTOM that arrived with the link: the dates are already known, so opening
   * the date picker over a screen the producer only clicked through to would be noise.
   */
  const skipInitialCustomModal = useRef(initialPeriodRange === 'CUSTOM');
  useEffect(() => {
    if (periodRange !== 'CUSTOM') return;
    if (skipInitialCustomModal.current) {
      skipInitialCustomModal.current = false;
      return;
    }

    setIsDialogOpen(true);
  }, [periodRange]);

  const periodRangeOptions = useMemo(
    () => [
      { value: 'TODAY', label: 'Hoje' },
      { value: 'LAST_7_DAYS', label: 'Ultimos 7 dias' },
      { value: 'LAST_15_DAYS', label: 'Ultimos 15 dias' },
      { value: 'LAST_30_DAYS', label: 'Ultimos 30 dias' },
      { value: 'CURRENT_MONTH', label: 'Mês atual' },
      { value: 'LAST_90_DAYS', label: 'Ultimos 90 dias' },
      {
        value: 'CUSTOM',
        label: 'Personalizado',
        onClick: () => setIsDialogOpen(true),
      },
    ],
    [],
  );

  return (
    <>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit)}
        className="grid gap-main grow lg:grow-[0.3] sm:grid-cols-[1fr_1fr_1fr_50px]"
      >
        <InputSelect
          name="userRole"
          control={control}
          label="Função"
          options={opcoesDisponiveis}
          /*
            `displayEmpty` porque o valor de "Tudo o que eu alcanço" é string vazia, e sem isto o MUI
            trata vazio como "nada selecionado" e desenha a caixa em branco — o modo que é DEFAULT
            apareceria como campo não preenchido.
          */
          displayEmpty
          defaultValue={userRoleDefaultValue}
          className="w-full"
        />
        <InputCommunities
          name="botConfigIds"
          control={control}
          onChangeCallback={formFieldsOnChangeCallback}
          className="w-full"
          selectedFiltersPage={selectedFiltersPage}
          /*
            Com papel escolhido, a lista é a de hoje: recortada por aquele papel. Sem papel, ela passa
            a ser recortada por PERMISSÃO de ver o painel — e é aí que a comunidade alcançada por
            grant entra, porque nenhum papel de comissão a cobre.

            Não é sempre por permissão: escolher "Afiliado" listaria comunidade onde a pessoa só é
            dona, e pedir a fatia de afiliado ali volta vazio. Cada modo tem o recorte que a pergunta
            dele admite.
          */
          {...(userRole
            ? { userRolesFilter: [userRole] }
            : {
                permissionFilter: {
                  feature: 'dashboard' as const,
                  action: 'view' as const,
                },
                /*
                  Permissão E escopo. O primeiro este componente sabe conferir; o segundo é resposta
                  do painel, porque depende de quais famílias de blocos existem.
                */
                idsElegiveis: comunidadesServidas,
              })}
          initialBotConfigIds={initialSelection?.botConfigIds}
        />
        {/*
          Touching this select is what retires a handed-over range, rather than the submit it schedules.
          The submit is debounced by half a second, so leaving the handed-over range and returning to it
          inside that window collapses both changes into ONE submission carrying the original name - which
          an equality check in onSubmit reads as "never left" and answers with the link's dates.
        */}
        <InputSelect
          name="periodRange"
          control={control}
          label="Período"
          options={periodRangeOptions}
          defaultValue={initialPeriodRange}
          onChangeCallback={() => {
            handedOverNamedRange.current = null;
            formFieldsOnChangeCallback();
          }}
          className="w-full"
        />
        {/*
          An explicit refresh asks for the period as it stands NOW, so a handed-over range stops applying.
          Without this, a report opened from a "Hoje" link and left open past midnight would keep querying
          yesterday however many times the producer pressed refresh, while the selector said "Hoje".
        */}
        <IconButton
          onClick={() => {
            handedOverNamedRange.current = null;
            formFieldsOnChangeCallback();
          }}
          aria-label="refresh"
          color="secondary"
          size="medium"
          className="hidden sm:block"
        >
          <RefreshIcon />
        </IconButton>
      </form>

      <VibModal
        isOpen={isDialogOpen}
        setIsOpen={setIsDialogOpen}
        title="Período personalizado"
      >
        <CustomPeriodRange
          setDashboardFilters={setFilters}
          setIsOpen={setIsDialogOpen}
        />
      </VibModal>
    </>
  );
}

/*
  `COLLABORATOR` fica de fora junto com `REFERRER`, e pelo mesmo motivo.

  Este filtro serializa o valor escolhido em `userRoles` da requisição de relatório, e do outro lado
  ele é lido como PAPEL DE COMISSÃO — o recorte por `commission_rate`. Colaboração não é papel de
  comissão: escolher a opção produziria relatório vazio ou recusado pelo DTO, com aparência de
  quebrado.

  A rota de saída é o escopo por produto (§6.4.3), não este seletor. Enquanto ele não chegar, a
  opção não existe.
*/
export const userRoleOptions = Object.entries(ParsedUserRole)
  .filter(
    (entry) =>
      entry[1] !== ParsedUserRole.REFERRER &&
      entry[1] !== ParsedUserRole.COLLABORATOR,
  )
  .map((entry) => ({
    label: entry[1],
    value: entry[0] as UserRole,
  }));

/**
 * The roles these filters actually offer, for whoever reads one off a link.
 *
 * Derived from the options rather than listed again, so a role dropped from the select cannot go on being
 * accepted from a URL - which is what let REFERRER through: it is a real application role, so validating
 * against the application accepted it, and the select had no option to show for it.
 */
export const supportedUserRoles = userRoleOptions.map((option) => option.value);

/**
 * A ausência de papel, como valor de `InputSelect`.
 *
 * String vazia porque o campo do formulário precisa de um valor, e vazio é o que `userRolesDoPedido`
 * e o `parseUserRole` já tratam como "nenhum papel" — o primeiro manda lista vazia, o segundo
 * devolve `undefined`. Nada precisou aprender um sentinela novo.
 */
export const SEM_PAPEL = '';

/**
 * As opções do seletor: "tudo o que eu alcanço" na frente, os papéis depois.
 *
 * Lista SEPARADA de `userRoleOptions`, e não ela com um item a mais, por dois motivos: o
 * `supportedUserRoles` — que valida papel vindo de link — continua sendo só papel de verdade, e o
 * default anterior indexava `userRoleOptions` por posição, então prefixar um item ali trocaria
 * coprodutor por dono em silêncio. O default saiu, mas a armadilha fica registrada: quem voltar a
 * indexar por posição precisa saber que existem duas listas.
 */
export const opcoesDeFuncao = [
  { label: 'Tudo o que eu alcanço', value: SEM_PAPEL },
  ...userRoleOptions,
];

/**
 * Se a pessoa TEM aquele papel em alguma comunidade, hoje.
 *
 * Responde pelas COMUNIDADES, e não pelos baldes, e a diferença não é estilo: o `GroupsContext`
 * remove de `groups.affiliated` a comunidade em que a pessoa também é coprodutora — deduplicação de
 * listagem, feita depois de `communities` ter sido montado. Quem afilia só numa comunidade que
 * também coproduz tem afiliação de verdade e balde vazio, e validar pelo balde descartaria o papel
 * lembrado dela: o painel voltaria mostrando a fatia de coprodutora sem dizer que trocou.
 *
 * `communities` é montado ANTES da deduplicação e carrega os dois papéis por comunidade, então é a
 * fonte honesta para esta pergunta.
 */
const temOPapel = (communities: UserCommunity[], papel: UserRole) =>
  communities.some((community) => community.userRoles.includes(papel));

/**
 * O papel de partida onde a ausência de papel NÃO é servida.
 *
 * Escolhe dono, coprodutor ou afiliado na ordem em que a pessoa tenha o papel. Para quem só alcança
 * comunidade por colaboração ele escolhe "Criador" — um papel que ela não tem em lugar nenhum, e a
 * lista abre vazia. É o defeito que "Tudo o que eu alcanço" conserta, e é por isso que ele continua
 * aqui: para os consumidores que ainda não sabem servir aquele modo, ter um papel errado de partida
 * é melhor que ter nenhum, porque nenhum, ali, é seleção que chega em widget que a recusa.
 */
const resolveUserRoleDefaultValue = (groups: Groups) => {
  if (groups.owner.length) return userRoleOptions[0].value;
  if (groups.coproducer.length) return userRoleOptions[1].value;
  if (groups.affiliated.length) return userRoleOptions[2].value;

  return userRoleOptions[0].value;
};
